import * as React from "react";
import { useQuery } from "react-query";
import { DataTable } from "components/DataTable/DataTable";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { TablePagination, Stack, Box } from "@mui/material";
import { FileTypesByClientReportItem, ReportFileInfo, MetricsService } from "gen/clients";

interface Props {
  reportId: string;
  jsonFile: ReportFileInfo;
}

const FileTypesByClientReportTable: React.FC<Props> = ({ reportId, jsonFile }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const {
    data: reportData,
    isLoading,
    error
  } = useQuery<FileTypesByClientReportItem[]>(["fileTypesByClientReportData", reportId, jsonFile.s3Key], {
    queryFn: async () => {
      if (!jsonFile.s3Key) {
        throw new Error("JSON file s3Key is missing");
      }

      const downloadResponse = await MetricsService.downloadReportJobFile({
        reportId,
        s3Key: encodeURIComponent(jsonFile.s3Key)
      });

      const response = await fetch(downloadResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch report data: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!jsonFile.s3Key,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });

  const handleChangePage = React.useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const displayData = React.useMemo(() => {
    if (!reportData) {
      return [];
    }
    const startIndex = page * rowsPerPage;
    return reportData.slice(startIndex, startIndex + rowsPerPage);
  }, [reportData, page, rowsPerPage]);

  return (
    <Stack>
      <DataTable
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        columns={[
          {
            id: "clientName",
            title: "Client",
            cell: (row: FileTypesByClientReportItem) => row.clientName
          },
          {
            id: "branchName",
            title: "Branch",
            cell: (row: FileTypesByClientReportItem) => row.branchName || "-"
          },
          {
            id: "salesRep",
            title: "Sales Rep",
            cell: (row: FileTypesByClientReportItem) => row.salesRep || "-"
          },
          {
            id: "numberOfExcel",
            title: "Number of Excel",
            cell: (row: FileTypesByClientReportItem) => row.numberOfExcel || 0
          },
          {
            id: "numberOfPdf",
            title: "Number of PDF",
            cell: (row: FileTypesByClientReportItem) => row.numberOfPdf || 0
          },
          {
            id: "numberOfPowerPoint",
            title: "Number of PowerPoint",
            cell: (row: FileTypesByClientReportItem) => row.numberOfPowerPoint || 0
          },
          {
            id: "numberOfTxt",
            title: "Number of TXT",
            cell: (row: FileTypesByClientReportItem) => row.numberOfTxt || 0
          },
          {
            id: "numberOfWord",
            title: "Number of Word",
            cell: (row: FileTypesByClientReportItem) => row.numberOfWord || 0
          },
          {
            id: "numberOfOther",
            title: "Number of Other",
            cell: (row: FileTypesByClientReportItem) => row.numberOfOther || 0
          }
        ]}
        data={displayData}
        rowKey={(row: FileTypesByClientReportItem) => row.clientId}
        dense={true}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <TablePagination
          component="div"
          count={reportData?.length || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Stack>
  );
};

export { FileTypesByClientReportTable };
